import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Link } from 'react-router'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { modifyProps } from '@launchpadlab/lp-hoc'
import * as effects from 'main/effects'
import * as actions from '../actions'
import { Spinner } from '@launchpadlab/lp-components'
import { default as ModalDialog } from 'components/ui/ModalDialog'
import ReactGA from 'react-ga4'
import * as apiActions from 'main/apiActions'
import * as Types from 'main/types'
import OrderReceipt from '../components/OrderReceipt'
import { selectors } from '../reducer'
import FieldTripPaymentInfoCard from '../components/FieldTripPaymentInfoCard'
import SocietyCheckoutLayout from '../../../layout/SocietyCheckoutLayout'
import { TICKETING_CHECKOUT_STEPS } from '../../types'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { routerActions } from 'react-router-redux'
import useModal from 'hooks/useModal'
import { displayCurrency } from 'utils'

const propTypes = {
  location: PropTypes.shape({ query: PropTypes.object }),
  ticketCart: Types.ticketCart,
  fetchOrCreateTicketCart: PropTypes.func.isRequired,
  fetchTicketCartPreview: PropTypes.func.isRequired,
  previewCart: Types.previewCart,
  setTicketCart: PropTypes.func.isRequired,
  removeTicketLineItem: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
}

const defaultProps = {}

function ViewCart({
  location: {
    query: { cartToken, activityId, backPath, lineItemId, addOnCategoryId },
  },
  ticketCart,
  previewCart,
  fetchOrCreateTicketCart,
  fetchTicketCartPreview,
  push
}) {
  useEffect(() => {
    ReactGA.event('begin_checkout', {
      currency: 'USD',
      value: ticketCart?.total,
      items: ticketCart?.lineItems?.map((cartItem) => ({
        item_id: cartItem.productId,
        item_name: cartItem.displayName,
        item_category: cartItem.productType,
        price: Number(
          cartItem.lineItemPrices
            ?.reduce((total, item) => total + item.price, 0)
            .toFixed(2)
        ),
        quantity: Number(
          cartItem.lineItemPrices?.reduce(
            (total, item) => total + item.quantity,
            0
          )
        ),
      })),
    })
  }, [])

  useEffect(() => {
    if (cartToken) {
      fetchOrCreateTicketCart(cartToken)
      fetchTicketCartPreview(cartToken)
    }
  }, [cartToken, fetchOrCreateTicketCart, fetchTicketCartPreview])

  const checkoutFlag = useFeatureFlagVariantKey('offer-membership-on-checkout')
  const addMembershipModal = useModal()

  if (!ticketCart) return <Spinner />

  const subpath = backPath || 'details'
  const backUrl = `/ticketing/${subpath}?cartToken=${cartToken}${
    activityId ? `&activityId=${activityId}` : ''
  }${lineItemId ? `&lineItemId=${lineItemId}` : ''}${
    addOnCategoryId ? `&addOnCategoryId=${addOnCategoryId}` : ''
  }`
  const forwardUrl = `/ticketing/pay${location.search}`

  const isFieldTripBooking = Boolean(ticketCart.schoolName)

  return (
    <SocietyCheckoutLayout
      progress={{ steps: TICKETING_CHECKOUT_STEPS, currentStep: 3 }}
      backUrl={backUrl}
      title="Shopping Cart"
      className="space-y-10"
    >
      {isFieldTripBooking && <FieldTripPaymentInfoCard />}
      <section className="p-5 mb-5 bg-white filter drop-shadow sawtooth-bottom">
        <OrderReceipt />
      </section>
      <section className="space-y-5">
        <span
          className={classnames('button-primary', 'button-next-step')}
          onClick={checkoutFlag && previewCart?.total < ticketCart?.total ? addMembershipModal.open : () => push(forwardUrl)}
        >
          Continue
        </span>
        <Link
          className={classnames(
            'button-primary',
            'button-next-step',
            'button-green-outline'
          )}
          to="/ticketing"
        >
          Add Another Item
        </Link>
      </section>
      {checkoutFlag && ticketCart && previewCart && <ModalDialog
        isOpen={addMembershipModal.isOpen}
        onClose={async ()=> {
          await effects.updateTicketCart(ticketCart.token, { withMembership: false })
          const searchData = new URLSearchParams(location.search)
          searchData.set('withMembership', false)
          push(`/ticketing/pay?${searchData.toString()}`)
        }}
        title="Become a Member and Save!"
        closeLabel="No, thank you."
        onConfirm={async ()=> {
          await effects.updateTicketCart(ticketCart.token, { withMembership: true })
          const searchData = new URLSearchParams(location.search)
          searchData.set('withMembership', true)
          push(`/ticketing/pay?${searchData.toString()}`)
        }}
        confirmLabel="Yes, add a Household Membership to my order"
        content={<div>
            <header className="space-y-1 leading-snug">Purchase a Household Membership and save <span className="font-bold text-xl text-primary-500">{displayCurrency(ticketCart.total - previewCart.total)}</span>
            {` `}on your tickets!</header>
            <p>With the #1 Zoo Membership in the Nation,members get more: more visits, more perks, and more value.{` `}
              A Household Membership ($205) often pays for itself after just 2 visits<sup>*</sup> and offers an easy way to enjoy the Zoo all year long.</p>
            <h4>Annual Household Membership Benefits</h4>
            <ul className="list-disc list-inside space-y-2">
              <li>Unlimited general Zoo admission for 2 adults and up to 6 children (ages 0-20) at the member&apos;s address</li>
              <li>A $150+ value in the exclusive monthly <a href="https://www.omahazoo.com/memberdays">member appreciation perks</a><sup>*</sup></li>
              <li>Free films at the Lozier Giant Screen Theater® (showtimes may vary)</li>
              <li>Discounts on classes, camps, and special events</li>
              <li>Disconted reciprocal privileges at more than 100 zoos nationwide</li>
              <li>5% off Zoo concessions, rides, and gift shop purchases (membership card and photo I.D. required)</li>
            </ul>
            <p className="mt-3">Your membership will be valid for one year from the date of purchase. You will receive a temporary digital membership card via email immediately after your purchase. Your permanent membership card will be mailed to you within two weeks.</p>
            <p>*Based on admission for a family of four. Actual savings may vary depending on visit frequency.</p>
          </div>}
        compact
      />}
    </SocietyCheckoutLayout>
  )
}

ViewCart.propTypes = propTypes
ViewCart.defaultProps = defaultProps

function mapStateToProps(state) {
  return { 
    ticketCart: selectors.ticketCart(state),
    previewCart: selectors.previewCart(state),
  }
}

const mapDispatchToProps = {
  fetchOrCreateTicketCart: apiActions.fetchOrCreateTicketCart,
  fetchTicketCartPreview: apiActions.fetchTicketCartPreview,
  setTicketCart: actions.setTicketCart,
  push: routerActions.push,
}

function modify() {
  return {
    removeTicketLineItem: ({ ticketCartToken, ticketLineItemId }) =>
      effects.destroyTicketLineItem(ticketCartToken, ticketLineItemId),
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  modifyProps(modify)
)(ViewCart)
